<template>
  <div class="settings-container">
    <div class="admin-card text-center">
      <h3 class="section-title mb-4">PROFILOVÁ FOTOGRAFIE</h3>

      <div class="profile-avatar-wrapper mx-auto mb-4">
        <img
          v-if="previewUrl || currentPhotoUrl"
          :src="previewUrl || currentPhotoUrl"
          alt="Avatar"
          class="avatar-img"
        />

        <div v-else class="avatar-placeholder">
          <svg
            class="placeholder-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>

        <div v-if="loading" class="loading-overlay">
          <div class="spinner-border text-danger" role="status"></div>
        </div>
      </div>

      <div class="actions d-flex justify-content-center gap-3">
        <input
          type="file"
          ref="fileInput"
          class="d-none"
          accept="image/*"
          @change="handleFileChange"
        />

        <button
          class="btn-red-glow btn-sm-custom"
          @click="triggerFileInput"
          :disabled="loading"
        >
          <svg
            class="icon-sm me-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
            ></path>
            <circle cx="12" cy="13" r="4"></circle>
          </svg>
          {{ previewUrl || currentPhotoUrl ? "ZMĚNIT FOTKU" : "NAHRÁT FOTKU" }}
        </button>

        <button
          v-if="currentPhotoUrl && !previewUrl"
          class="btn-outline-danger btn-sm-custom"
          @click="deleteImage"
          :disabled="loading"
          title="Odstranit fotku"
        >
          <svg
            class="icon-sm"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6"></polyline>
            <path
              d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
            ></path>
          </svg>
        </button>

        <button
          v-if="previewUrl"
          class="btn-outline-danger btn-sm-custom"
          @click="cancelPreview"
          title="Zrušit výběr"
        >
          <svg
            class="icon-sm"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <transition name="fade">
        <div v-if="previewUrl" class="mt-4">
          <p class="text-muted small mb-2">Fotka je připravena k nahrání</p>
          <button
            class="btn-red-glow w-100"
            @click="uploadImage"
            :disabled="loading"
          >
            ULOŽIT ZMĚNU
          </button>
        </div>
      </transition>

      <transition name="fade">
        <div v-if="successMessage" class="alert-box success mt-3 small-alert">
          ✅ {{ successMessage }}
        </div>
      </transition>
      <transition name="fade">
        <div v-if="errorMessage" class="alert-box error mt-3 small-alert">
          ⚠️ {{ errorMessage }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { auth, db, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export default {
  name: "UserProfileSettings",
  data() {
    return {
      currentPhotoUrl: null,
      previewUrl: null,
      selectedFile: null,
      loading: false,
      successMessage: "",
      errorMessage: "",
      userId: auth.currentUser ? auth.currentUser.uid : null,
    };
  },
  async mounted() {
    await this.fetchUserProfile();
  },
  methods: {
    // 1. Načtení profilu
    async fetchUserProfile() {
      if (!this.userId) return;
      try {
        const userDoc = await getDoc(doc(db, "users", this.userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          this.currentPhotoUrl = data.profilePicture || null;
        }
      } catch (error) {
        console.error("Chyba načítání profilu:", error);
      }
    },

    // 2. Kliknutí na skrytý input
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // 3. Výběr souboru a náhled
    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validace velikosti (např. max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this.errorMessage = "Obrázek je příliš velký (max 2MB).";
        return;
      }

      // Validace typu
      if (!file.type.startsWith("image/")) {
        this.errorMessage = "Vyberte prosím obrázek.";
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
      this.errorMessage = "";
      this.successMessage = "";
    },

    // 4. Zrušení náhledu
    cancelPreview() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = "";
    },

    // 5. Nahrání na Firebase Storage
    async uploadImage() {
      if (!this.selectedFile || !this.userId) return;
      this.loading = true;
      this.errorMessage = "";

      try {
        // ❌ SMAZAT TENTO ŘÁDEK (způsoboval chybu):
        // const fileExtension = this.selectedFile.name.split('.').pop();

        // ✅ Ponechat pouze toto:
        // Cesta: users/{userId}/profile_avatar
        // Používáme fixní název bez přípony, aby se stará fotka přepsala
        const storageRef = ref(storage, `users/${this.userId}/profile_avatar`);

        await uploadBytes(storageRef, this.selectedFile);
        const downloadUrl = await getDownloadURL(storageRef);

        // Uložení URL do Firestore
        await updateDoc(doc(db, "users", this.userId), {
          profilePicture: downloadUrl,
        });

        this.currentPhotoUrl = downloadUrl;
        this.successMessage = "Profilová fotka byla aktualizována.";
        this.cancelPreview();
      } catch (error) {
        console.error("Upload error:", error);
        this.errorMessage = "Chyba při nahrávání: " + error.message;
      } finally {
        this.loading = false;
      }
    },

    // 6. Smazání fotky
    async deleteImage() {
      if (!confirm("Opravdu chcete odstranit profilovou fotku?")) return;
      this.loading = true;

      try {
        // Smazat ze Storage
        const storageRef = ref(storage, `users/${this.userId}/profile_avatar`);
        await deleteObject(storageRef).catch((error) => {
          console.warn(
            "Obrázek ve storage nebyl nalezen, mažu jen odkaz.",
            error
          );
        });

        // Smazat odkaz z Firestore
        await updateDoc(doc(db, "users", this.userId), {
          profilePicture: null, // nebo deleteField()
        });

        this.currentPhotoUrl = null;
        this.successMessage = "Fotka byla odstraněna.";
      } catch (error) {
        console.error("Delete error:", error);
        this.errorMessage = "Chyba při mazání.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.settings-container {
  max-width: 500px;
  margin: 0 auto;
}

/* Avatar Wrapper */
.profile-avatar-wrapper {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: 3px solid #333;
  background-color: #000;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(220, 53, 69, 0.2); /* Červená záře */
  transition: border-color 0.3s;
}

.profile-avatar-wrapper:hover {
  border-color: #dc3545;
}

/* Obrázek */
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Placeholder */
.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
}

.placeholder-icon {
  width: 60px;
  height: 60px;
  stroke: #444;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* Tlačítka */
.btn-sm-custom {
  padding: 8px 16px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.small-alert {
  padding: 10px;
  font-size: 0.9rem;
}

.icon-sm {
  width: 18px;
  height: 18px;
}
</style>
